import React, { useEffect, useContext, useState, useRef } from 'react';
import styled from 'styled-components/macro';
import deepClone from 'deep-clone';
import GTM from '@mirinae/apis/GoogleAnalytics';

import { fontkoKR } from '@mirinae/react-ui';
import { os } from '@mirinae/js-utils/utils/userAgent';

import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import { setAnimStep } from '@mirinae/lessons/editor/Editor';
import { getMetricsForStrings } from '@mirinae/lessons/modules/libs/layoutLib';
import { fontDefault, colorMap, styleMap, lessonsPalette, lessonsSizes, computeMobilePatternLayout } from '@mirinae/lessons/modules/defines/styles';

import StepperButton from '@mirinae/lessons/components/buttons/StepperButton';
import stemifyArrow from '@mirinae/lessons/assets/images/remove-arrow.svg';
import bracket1 from '@mirinae/lessons/assets/images/bracket-1.svg';
import bracket2 from '@mirinae/lessons/assets/images/bracket-2.svg';
import bracket3 from '@mirinae/lessons/assets/images/bracket-3.svg';
import speakIcon from '@mirinae/lessons/assets/images/speak.svg';

import { FormattedText, textFromParts } from '@mirinae/lessons/components/lesson/elements/Text';
import { HelpBubble, registerHelp } from '@mirinae/lessons/components/lesson/Help';
import { playTTS } from '@mirinae/lessons/modules/libs/lessonLib';

const isMobile = !os.isOthers;

const PatternStage = styled.div`
    width: ${props => props.geom.stage.width}px;
    height: ${props => props.geom.stage.height}px;
    ${props => (isMobile ? `
        position: relative;
    ` : `
        position: absolute;
        left: ${props.geom.stage.x}px;
        top: ${props.geom.stage.y}px;
    `)}
`;

const AnimScroller = styled.div`
    position: absolute;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-behavior: smooth;
    left: ${props => props.geom.x}px;
    top: ${props => props.geom.y}px;
    width: ${props => props.geom.width}px; 
    height: ${props => props.geom.height}px;
`;

const AnimSVG = styled.svg`
    opacity: ${props => (props.startHidden ? 0 : 1)};
    // ${props => os.isOthers && `
    //     position: absolute;
    //     left: ${props.box.svgBox.x}px;
    //     top: ${props.box.svgBox.y}px;
    // `}
`;

const SpeakIcon = styled.image`
    opacity: 0;
    transition: opacity 0.6s linear .6s;
    cursor: pointer;
`;

const ControlRow = styled.div`
    position: absolute;
    width: ${props => props.geom.width}px;
    height: ${props => props.geom.height}px;
    left: ${props => props.geom.x}px;
    top: ${props => props.geom.y}px;
`;

const StepperWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: ${props => (props.steps.length > 1 ? 'space-between' : 'center')};
    width: 100%;
    padding: 5px;
`;

const Stepper = ({ steps, stepIndex, onNext, onBack, onReplay }) => {
    const [state, setState] = useState(stepIndex.current === 0 ? 'start' : (stepIndex.current === steps.length - 1 ? 'end' : 'middle'));
    const player = useContext(PlayerContext);

    const buttonClicked = (e, handler) => {
        handler(e);
        setState(stepIndex.current === 0 ? 'start' : (stepIndex.current === steps.length - 1 ? 'end' : 'middle'));
    };

    const isDisabled = (condition) => state === condition && !player.currentPage.hasExampleAnimations;

    return (
        <StepperWrapper steps={steps}>
            <StepperButton className="step-back" data-t={`${stepIndex.current}-step-back`} disabled={isDisabled('start')} onClick={e => buttonClicked(e, onBack)} icon="back" />
            { steps.length > 1 && state === 'end' && <StepperButton className="step-replay" data-t={`${stepIndex.current}-step-forward`} onClick={e => buttonClicked(e, onReplay)} icon="replay" />}
            { steps.length > 1 && <StepperButton className="step-fwd" data-t={`${stepIndex.current}-step-forward`} disabled={isDisabled('end')} onClick={e => buttonClicked(e, onNext)} state="active" icon="next" />}
        </StepperWrapper>
    );
};

const StepGroup = styled.g`
    display: none;
`;

// initial default styles for all pattern DOM nodes sets opacity to 0 (since all steps are rendered at once)
//   with default opacity & transform animated transitions which will be updated under script control as
//   process steps are bumped
const WordG = styled.g`
    opacity: 0;
    transform: translate(${props => props.part.incoming.startX}px, 0px);
`;

const Word = styled.text`
    fill: ${props => colorMap[props.part.color]};
    ${props => styleMap[props.part.color] && styleMap[props.part.color]};
`;

// https://css-tricks.com/controlling-css-animations-transitions-javascript/
// -webkit-transform: translate3d(x, y, z)

// have to wrap <text> in <g> since CSS transforms don't work on <text> in bloddy Safari!!!!
const PhraseWord = ({ nodeRef, part, element, children }) => {
    const shift = part.color === 'xxplaceHolder' ? { dx: -5, dy: 0 } : { dx: 0, dy: 0 }; // old 'placeHolder' seems to be unused, disabling for alternate use
    return part.value ? (
        <WordG ref={nodeRef} part={part} element={element}>
            <Word part={part} element={element} dx={shift.dx} dy={shift.dy}>{part.value}{children}</Word>
        </WordG>
    ) : null;
};

// return fontSize for the given part and language code under possible styleMap CSS style mods
const getStyledFontSize = (part, lang) => {
    let fontSize = lessonsSizes.pattern[`${lang}FontSize`];
    if (styleMap[part.color]) {
        const match = styleMap[part.color].match(/font-size:\s+(\d+)(%|px)/);
        if (match && match[2] === '%') fontSize *= Number(match[1]) / 100;
        if (match && match[2] === 'px') fontSize = Number(match[1]);
    }
    return fontSize;
};

const KoreanParts = styled.g`
    font-family: ${fontkoKR};
    font-style: normal;
    font-weight: bold;
    font-size: ${lessonsSizes.pattern.koreanFontSize}px;
    line-height: 56px;
`;

const EnglishParts = styled.g`
    font-family: ${fontDefault};
    font-style: normal;
    font-weight: 600;
    font-size: ${lessonsSizes.pattern.englishFontSize}px;
    line-height: 20px;
    color: ${lessonsPalette.grey[2]};
`;

const SubtextG = styled.g`
    opacity: 0;
    transform: translate(${props => props.subtext.incoming.endX}px, 0px);
`;

const SubtextGroup = styled.g`
    font-family: ${fontDefault};
    font-style: normal;
    font-weight: 500;
    font-size: ${lessonsSizes.pattern.subtextFontSize}px;
    line-height: 20px;
    fill: ${lessonsPalette.patternSubtext};    
`;

const Subtext = ({ nodeRef, subtext }) => (
    <SubtextG ref={nodeRef} subtext={subtext}>
        <text>{subtext.text}</text>
        <path d={`m${subtext.width / 2 - subtext.cs.width / 2},9 l${subtext.cs.width},0`} strokeWidth="1" stroke={lessonsPalette.patternSubtextLine} />
    </SubtextG>
);

const NarrationParts = styled.div`
    visibility: hidden;
    position: absolute;
    width: ${props => props.geom.width}px;
    height: ${props => props.geom.height}px;
    left: ${props => props.geom.x}px;
    top: ${props => props.geom.y}px;
`;

const NarrationBox = styled.div`
    display: flex;
    flex-direction: column;
    ${os.isOthers ? `
        padding-top: 40px;  
    ` : `
        padding-top: 5px;
    `}
`;

const NarrationBubble = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    line-height: 1.35;
    // max-width: ${props => props.box.narrationBox.width - 30}px;
    padding: 15px 20px;
    background: rgba(223, 223, 223, 0.3);
    border-radius: ${props => (props.dir === 'up' ? '5px 30px 30px 30px' : '30px 30px 30px 5px')};
    //
    font-family: ${fontDefault};
    font-weight: normal;
    font-size: 16px;
    color: ${lessonsPalette.primary.black};
    //
    opacity: 0;
    transition: opacity 250ms linear;
    
    ${props => (os.isOthers ? `
        margin: 12px 15px;
        width: ${props.orientation === 'vertical' ? '100%' : 'fit-content'}};
    ` : `
        margin: 8px 0px;
        // min-width: ${lessonsSizes.pattern.narrationBox.width}px;    
    `)}
`;

const Narration = ({ nodeRef, part, index, fadeIn, box, orientation }) => {

    return (
        <NarrationBubble ref={nodeRef} fadeIn={fadeIn} dir={index === 0 && orientation !== 'vertical' && !isMobile ? 'down' : 'up'} box={box} orientation={orientation}>
            <FormattedText text={part.value} styles={part.styles} links={part.links} />
        </NarrationBubble>
    );
};

const InspectedJamo = styled.text`
    fill: ${lessonsPalette.primary.white};
    font-family: ${fontkoKR};
    font-weight: bold;
    font-size: ${lessonsSizes.pattern.jamoBox.fontSize}px;
    display: flex;
    align-items: center;
    color: ${lessonsPalette.primary.white};
`;

const JamoBox = styled.g`
`;

const RisingJamo = ({ nodeRef, f, x, y, children }) => (
    <JamoBox transform={`translate(${x}, ${y})`}>
        <g className="rising-jamo" ref={nodeRef} style={{ opacity: 0 }}>
            <rect {...f.jamoBox} />
            <InspectedJamo dx="2" dy="-6">{ children }</InspectedJamo>
        </g>
    </JamoBox>
);

const AbbreviationBracket = styled.image`
    opacity: 0;
`;

const StemifyArrow = styled.image`
    opacity: 0;
    transition: opacity 350ms linear;
`;

// run through the field list & their parts, computing needed layout geometry
//  return a copy of the field list with geometry & other layout structures inserted into the copy
export const computeAnimationLayout = (element, geomElement) => {
    // parse part commands, adds them in as props on part & grabs any new section-related commands
    const parseCommands = (p, section, field) => {
        if (p.command)
            p.command.split(',').forEach(anno => {
                const [cmdstr, params] = anno.split('=');
                const numParam = parseFloat(params);
                const cmd = cmdstr.trim().replace(/flyin/i, 'flyIn');
                p[cmd] = Number.isNaN(numParam) ? (params || true) : numParam;
                if (cmd === 'after' && p[cmd] < 10) p[cmd] *= 1000; // normalize to ms
                section.cmds[cmd] = section.cmds[cmd] ? section.cmds[cmd] : p[cmd];
                field.cmds[cmd] = field.cmds[cmd] ? field.cmds[cmd] : p[cmd];
            });
    };
    const isSeparator = (p) => p.color.match(/separator|separate|merge/);

    // short pass to prep field clones & compute overall section count
    let sectionCount = 0;
    element.fields.filter(f => f.name.toLowerCase() === 'example').forEach(f => { f.label = 'Example'; f.example = true; f.hidden = true; }); // unhide example field, this is a hack, should flag it explicitly
    element.fields.filter(ef => !ef.hidden && ef.type === 'parts').map(ef => {
        ef.sectionCount = {};
        ['koreanParts', 'englishParts'].forEach(lang => {
            const origParts = ef.parts[lang]; ef.parts[lang] = [];
            let separatorCount = 0;
            origParts.forEach((p, i) => {
                if (isSeparator(p)) {
                    separatorCount += 1;
                    if (i === 0 || isSeparator(origParts[i - 1]))
                        ef.parts[lang].push({ value: '', color: lang === 'koreanParts' ? 'addition' : 'english' }); // add blank parts for empty sections
                }
                ef.parts[lang].push(p);
                if (i === origParts.length - 1 && isSeparator(origParts[i]))
                    ef.parts[lang].push({ value: '', color: lang === 'koreanParts' ? 'addition' : 'english' });
            });
            ef.sectionCount[lang] = separatorCount + 1;
            sectionCount = Math.max(sectionCount, separatorCount + 1);
        });
    });

    // ensure all sections have parts
    element.fields.filter(ef => ef.sectionCount).forEach(ef => {
        ['koreanParts', 'englishParts'].forEach(lang => {
            for (let msi = ef.sectionCount[lang]; msi < sectionCount; msi += 1) {
                ef.parts[lang].push({ value: '', color: 'separate' });
                ef.parts[lang].push({ value: '', color: lang === 'koreanParts' ? 'addition' : 'english' });
                ef.changed = true;
            }
        });
    });

    // take a field clones so reruns of this process start clean again
    // avoiding copying of DOM ref when open in editor
    const fields = element.fields.filter(ef => ef.sectionCount).map(ef => deepClone({ ...ef, ref: null }));

    let maxWidth = 0; let hasSubtext = false;
    let boxDimensions = lessonsSizes.pattern[element.fieldsByName.layout ? element.fieldsByName.layout.value.toLowerCase() : 'horizontal'];
    // adjust pattern-box geom for mobile, depending on screen sizes
    if (isMobile) {
        computeMobilePatternLayout(geomElement || element);
        boxDimensions = lessonsSizes.pattern;
    }

    //  make a pass to compute field horizontal geometry
    // the parts in a field are split into sections, to contain the phrases, patterns, endings, etc., so they
    // can be separately aligned.  Split, for now, is via a separator part, marked with the 'separator' color
    // console.log('** recomputing pattern geom ***');
    const sectionInit = { width: 0, parts: [], cmds: {}, outgoing: {}, incoming: {}, centeringSectionIndex: 0,
        korean: { text: '', parts: [], width: 0, outgoing: {}, incoming: {}, removedParts: [] },
        english: { text: '', parts: [], width: 0, outgoing: {}, incoming: {}, removedParts: [] } };
    fields.forEach((f, fi) => {
        if (!f.hidden && f.type === 'parts') {
            // init field's section array
            f.sections = [...Array(sectionCount)].map((_, i) => ({ ...(deepClone(sectionInit)), index: i }));
            element.fields[f.index].stepFieldIndex = fi;
            f.incoming = {};
            f.outgoing = {};
            f.korean = { width: 0, offset: 0 }; f.english = { width: 0, offset: 0 };
            f.cmds = {};
            f.centeringSections = [{ width: 0, korean: { width: 0 }, english: { width: 0 }, subtext: { width: 0, text: '' } }];
            // gather text metrics for the field
            f.remainingParts = []; // after removing elided parts (like 다 during stemming)
            ['korean', 'english'].forEach(lang => {
                const parts = f.parts[`${lang}Parts`];
                if (parts) {
                    // let x = 0; // running x offset for parts in entire field
                    let sx = 0; // running x offset for parts in each section
                    let sectionIndex = 0;
                    let indexInSection = 0;
                    let centeringSectionIndex = 0;
                    let s = null; // current accumulating section object
                    let cs = null;
                    f.parts[`${lang}Parts`] = [];
                    parts.forEach((p, pi) => {
                        // construct pattern sections at 'separator' parts, tracking per-section metrics & content
                        if (p.color === 'space') p.value = ' ';
                        p.index = indexInSection;
                        p.partIndex = pi;
                        indexInSection += 1;
                        if (!s) {
                            f.sections[sectionIndex].centeringSectionIndex = centeringSectionIndex;
                            if (!f.centeringSections[centeringSectionIndex])
                                f.centeringSections[centeringSectionIndex] = { width: 0, korean: { width: 0 }, english: { width: 0 }, subtext: { width: 0, text: '' } };
                        }
                        s = f.sections[sectionIndex];
                        cs = f.centeringSections[s.centeringSectionIndex];
                        if (isSeparator(p)) {
                            s[lang].width = Math.max(sx, s[lang].width);
                            s.width = Math.max(sx, s.width);
                            cs[lang].width += s[lang].width;
                            cs.width = Math.max(cs.width, cs[lang].width, cs.subtext.width);
                            if (p.color !== 'merge') {
                                centeringSectionIndex += 1;
                            }
                            sectionIndex += 1;
                            sx = 0;
                            indexInSection = 0;
                            s = null;
                        } else { // if (p.value) {
                            p.metrics = getMetricsForStrings([p.value], `lesson-${lang}-phrase`, getStyledFontSize(p, lang))[0];
                            // parse & check part commands
                            parseCommands(p, s, f);
                            p.sectionX = sx;
                            p.sectionIndex = sectionIndex;
                            // ignore width contribution of centered parts in containing section
                            if (!p.center) sx += p.metrics.width;
                            s[lang].text += p.value;
                            s[lang].parts.push(p);
                            s.parts.push(p);
                            // track any removing parts & tag remaining with their move DXes to close up the gaps after elision
                            if (p.remove) {
                                s[lang].removedParts.push(p);
                            }
                            if (p.st) {
                                // explanation subtext, one per centering section, compute its width to ensure cs is wide enough
                                cs.subtext = {
                                    width: getMetricsForStrings([p.st], 'lesson-pattern-subtext', lessonsSizes.pattern.subtextFontSize)[0].width,
                                    text: p.st,
                                    cs,
                                };
                                hasSubtext = true;
                            }
                            p.outgoing = {};
                            p.incoming = {};
                            f.parts[`${lang}Parts`].push(p);
                        }
                        p.sectionIndex = sectionIndex;
                    });
                    if (s) {
                        s[lang].width = Math.max(sx, s[lang].width);
                        s.width = Math.max(sx, s.width);
                        cs[lang].width += s[lang].width;
                        cs.width = Math.max(cs.width, cs[lang].width, cs.subtext.width);
                    }
                }
            });
            // compute cummulative section offsets for entire field
            f.width = 0;
            f.centeringSections.forEach((cs, i) => {
                const gap = /* i > 0 */ f.width > 0 && cs.width > 0 ? lessonsSizes.pattern.sectionDx : 0;
                cs.offset = f.width + gap;
                f.width = cs.offset + cs.width;
                cs.center = cs.offset + cs.width / 2;
                // f.width += cs.width + (i <= f.centeringSections.length - 1 && cs.width > 0 ? lessonsSizes.pattern.sectionDx : 0);
                let so = 0; let sko = 0; let seo = 0;
                f.sections.filter(s => s.centeringSectionIndex === i).forEach(s => {
                    s.csOffset = so; so += s.width;
                    s.korean.csOffset = sko; sko += s.korean.width;
                    s.english.csOffset = seo; seo += s.english.width;
                });
            });
            let so = 0; let sko = 0; let seo = 0;
            f.sections.forEach((s, i) => {
                const gap = i > 0 && s.centeringSectionIndex === 0 && s.width > 0 ? lessonsSizes.pattern.sectionDx : 0;
                s.offset = so + gap; so = s.offset + s.width;
                s.korean.offset = sko + gap; sko = s.korean.offset + s.korean.width;
                s.english.offset = seo + gap; seo = s.english.offset + s.english.width;
            });
            maxWidth = Math.max(maxWidth, f.width);
        }
    });

    // make another pass to compute incoming & outgoing placement, motion & crossfades from one field to the next
    let prevF = fields[0];
    fields.forEach(f => {
        if (!f.hidden && f.type === 'parts') { // only visible part fields
            f.originX = Math.max(maxWidth, boxDimensions.patternBox.width) / 2 - f.width / 2;
            f.sections.forEach((is, i) => {
                const ics = f.centeringSections[is.centeringSectionIndex];
                const os = prevF.sections[i]; // in & out same for first field
                const ocs = prevF.centeringSections[os.centeringSectionIndex];
                ['korean', 'english'].forEach(lang => {
                    // compute outgoing start & end offsets for prior field section and incoming start & end offsets for this section
                    // such that start centers align on outgoing and end centers align on incoming sections
                    const endCenter = f.originX + ics.center - ics[lang].width / 2 + is[lang].csOffset + is[lang].width / 2;
                    const startCenter = prevF.originX + ocs.center - ocs[lang].width / 2 + os[lang].csOffset + os[lang].width / 2;
                    const fieldCenter = f.originX + f.width / 2;

                    
                    is[lang].incoming.startX = startCenter - is[lang].width / 2 + (os[lang].outgoing.remainingPartsDx || 0);
                    os[lang].outgoing.startX = startCenter - os[lang].width / 2;
                    is[lang].incoming.endX = endCenter - is[lang].width / 2;
                    os[lang].outgoing.endX = endCenter - os[lang].width / 2;

                    const noFade = is[lang].text === os[lang].text && f.index > 0;

                    // run over parts & set their individual startX & endX coordinates
                    is[lang].parts.forEach(ip => {
                        ip.incoming.startX = is[lang].incoming.startX + ip.sectionX;
                        ip.incoming.endX = (ip.center ? fieldCenter - ip.metrics.width / 2 : is[lang].incoming.endX) + ip.sectionX;
                        ip.incoming.noFade = noFade;
                    });
                    os[lang].parts.forEach(op => {
                        op.outgoing.startX = (op.center ? fieldCenter - op.metrics.width / 2 : os[lang].outgoing.startX) + op.sectionX;
                        op.outgoing.endX = os[lang].outgoing.endX + op.sectionX;
                        op.outgoing.noFade = noFade;
                    });
                    // compute dx moves for remaining parts if any removes
                    if (is[lang].removedParts.length > 0) {
                        const sectionRemainingParts = is[lang].parts.filter(p => !p.remove);
                        f.remainingParts = (f.remainingParts || []).concat(sectionRemainingParts);
                        const remainingPartsDx = (is[lang].width - sectionRemainingParts.reduce((a, p) => a + p.metrics.width, 0)) / 2;
                        f.sections.filter(s => s !== is && s.centeringSectionIndex === is.centeringSectionIndex)
                            .forEach(s => {
                                s[lang].parts.forEach(rp => { if (!rp.remove) f.remainingParts.push(rp); });
                                s[lang].outgoing.remainingPartsDx = remainingPartsDx;
                            });
                        f.remainingParts.forEach(p => {
                            p.remainingPartsDx = remainingPartsDx;
                        });
                        // close up surround centering sections if the centering-section with elisions is now smaller  (disabled for now, have to figure subsequent step adjusts)
                        // if (false && remainingPartsDx * 2 >= f.centeringSections[is.centeringSectionIndex].width) {
                        //     f.sections.filter(s => s.centeringSectionIndex !== is.centeringSectionIndex)
                        //         .forEach(s => {
                        //             const rpdx = s.centeringSectionIndex < is.centeringSectionIndex ? remainingPartsDx : -remainingPartsDx;
                        //             ['korean', 'english'].forEach(lang => {
                        //                 s[lang].parts.forEach(rp => {
                        //                     f.remainingParts.push(rp);
                        //                     rp.remainingPartsDx = rpdx;
                        //                 });
                        //                 s[lang].outgoing.remainingPartsDx = rpdx;
                        //             });
                        //         });
                        //     // f.centeringSections.forEach((cs, i) => {  ??
                        //     //     if (i !== is.centeringSectionIndex)
                        //     //         cs.center += i < is.centeringSectionIndex ? remainingPartsDx : -remainingPartsDx;
                        //     // });
                        // }
                    }
                });
            });

            // position any subtext
            f.centeringSections.forEach((ics, i) => {
                const ocs = prevF.centeringSections[i] || ics;
                const endCenter = f.originX + ics.center;
                const startCenter = prevF.originX + ocs.center;

                ics.subtext.incoming = { startX: startCenter - ics.subtext.width / 2, endX: endCenter - ics.subtext.width / 2 };
                ocs.subtext.outgoing = { startX: startCenter - ocs.subtext.width / 2, endX: endCenter - ocs.subtext.width / 2 };

                ics.subtext.fadeDuration = ics.subtext.text === ocs.subtext.text && f.index > 0 ? 0 : 350;
                ics.subtext.transformDuration = ics.subtext.incoming.startX === ics.subtext.incoming.endX && f.index > 0 ? 0 : 650;

                if (ics.width !== ocs.width || ics.subtext.width !== ocs.subtext.width)
                    f.scrollPoint = f.originX + ics.offset;
            });

            // default animation settings
            const fadeDuration = 650;
            const fadeDelay = 0;
            const transformDuration = 650;
            const transformDelay = 0;

            Object.assign(f.outgoing, { fadeDuration: 350, fadeDelay, transformDuration, transformDelay });
            Object.assign(f.incoming, { fadeDelay, fadeDuration, transformDuration, transformDelay });

            // console.log(f);
            // console.log(prevF);
            prevF = f;
        }
    });
    //
    return [fields, maxWidth, hasSubtext];
};

const PatternAnimation = ({ element, geomElement, next, back, width, autoFirstStep, startHidden, setRef, example }) => {
    const player = useContext(PlayerContext);
    const [steps, setSteps] = useState([]);
    const [layout, setLayout] = useState({});
    const [showControls, setShowControls] = useState(true);
    const animScroller = useRef(null);
    const startStep = new URLSearchParams(window.location.search).get('as');
    const stepIndex = useRef(startStep !== null ? Number.parseInt(startStep, 10) : 0);

    const narrationIndex = useRef(0);
    const orientation = element.fieldsByName.layout ? element.fieldsByName.layout.value.toLowerCase() : 'horizontal';
    const boxDimensions = isMobile ? lessonsSizes.pattern : (lessonsSizes.pattern[orientation]);

    // compute the overall layout geometry & display parts
    useEffect(() => {
        const [fields, maxWidth, hasSubtext] = computeAnimationLayout(element, geomElement);

        // another pass to allocate fields to steps & handle any special commands
        const applicationSteps = [];
        let prevActionStep = null;
        // let prevPartsToGo = null;
        fields.forEach(f => {
            if (!f.hidden && f.type === 'parts') { // also ignore summary for now
                // gather text metrics for the field
                if (f.parts.koreanParts) {
                    f.parts.koreanParts.forEach((p, i) => {
                        p.tag = `-korean-${i}`;
                        // process part-specific commands
                        if (p.remove) {
                            // syllable or jamo elision, such as stemming verb by removing 다
                            f.partsToGo = (f.partsToGo || []).concat({ part: p });
                            p.showArrow = true;
                            p.arrowOffset = f.centeringSections[f.sections[p.sectionIndex].centeringSectionIndex].subtext.text ? 25 : 0;
                            f.centeringSections[f.sections[p.sectionIndex].centeringSectionIndex].subtext.remove = true;
                        }
                        if (p.inspect) {
                            // inspecting jamo(s), usually last vowel or consonant before an attaching pattern
                            // causes rising box containing determining jamo
                            // p.inspect has jamo(s) being inspected, could be multiple, and mayb include ->, to be turned into arrow symbol
                            f.inspectPart = p;
                            f.inspectJamo = p.inspect.split(/->/).map(j => j.trim()).join('→');
                            const jamoBoxWidth = 6 + getMetricsForStrings([f.inspectJamo], 'lesson-korean-phrase', lessonsSizes.pattern.jamoBox.fontSize)[0].width;
                            f.inspectOffset = p.incoming.endX + p.metrics.width / 2 - jamoBoxWidth / 2;
                            f.jamoBox = { ...lessonsSizes.pattern.jamoBox, width: jamoBoxWidth };
                        }
                        if (p.checkStart || p.check || p.check1 || p.check2 || p.check3) {
                            // place check bracket over 1, 2 or 3 syllables starting at this part (retain original 'checkStart' & 'check' as 2 syllables)
                            f.checkStartPart = p;
                            f.bracketHref = p.check3 ? bracket3 : (p.check1 ? bracket1 : bracket2);
                        }
                    });
                }
                // same for English parts
                if (f.parts.englishParts) {
                    f.parts.englishParts.forEach((p, i) => {
                        p.tag = `-english-${i}`;
                        if (p.remove) {
                            // part elision, such as removing an optional clause
                            f.partsToGo = (f.partsToGo || []).concat({ part: p });
                            // any english part-leaving implies any subtexts in the part's centering section should also fly (this should only be if the whole CS empties)
                            f.centeringSections[f.sections[p.sectionIndex].centeringSectionIndex].subtext.remove = true;
                        }
                    });
                }

                // figure step involvement for the field, pick up needed state-specific metrics, indexes, etc.
                f.states.forEach(state => {
                    let step = { index: applicationSteps.length, field: f, state, prevActionStep };
                    if (state !== 'narrative')
                        prevActionStep = step;
                    // handle commands
                    if (f.inspectPart) {
                        step.state = 'inspect';
                        step.inspectX = f.inspectOffset;
                        step.inspect = true;
                        if (f.parts.narration.length > 1) {
                            step.narrations = [f.parts.narration[0]];
                            step.inspect = !f.cmds.delay;
                            applicationSteps.push(step);
                            step = { ...step, index: applicationSteps.length, state: 'narrative', parentStep: step, prevActionStep,
                                inspect: f.cmds.delay, wasInspecting: !f.cmds.delay, narrations: f.parts.narration };
                        } else {
                            step.narrations = f.parts.narration;
                        }
                    }
                    if (f.checkStartPart) {
                        step.state = 'checkAbbreviation';
                        step.checkX = f.checkStartPart.incoming.endX;
                    }

                    // add step
                    applicationSteps.push(step);
                    if (f.parts.koreanParts)
                        element.lastKoreanPartStep = step.index;
                    // add step narration & any builds if more than one narration part for this field
                    if (!step.narrations && f.parts.narration /* && !(f.hiddenParts && f.hiddenParts.includes('narration')) */) {
                        let narrations = [];
                        if (f.parts.narration[0].value[0] === '+' && step.index > 0) {
                            f.parts.narration[0].value = f.parts.narration[0].value.substring(1);
                            if (orientation === 'horizontal' && !isMobile) {
                                // '+' prefix means this narration adds to prior, copy across prior narration (but only in desktop horizontal form)
                                narrations = narrations.concat(applicationSteps[step.index - 1].narrations || []);
                            }
                        }
                        narrations = narrations.concat(f.parts.narration[0]);
                        step.narrations = narrations;
                        f.parts.narration.slice(1).forEach(n => {
                            narrations = [...narrations].concat(n);
                            applicationSteps.push({ ...step, index: applicationSteps.length, parentStep: step, state: 'narrative', narrations });
                        });
                    }
                });
            }
        });

        // compute overall pattern-application area layout & scale SVG box to fit max-width step, down to a minimum downscaling, when the pattern area is scrollable
        const { patternBox } = boxDimensions;
        const animWidth = (width || maxWidth) + 4; // plus a little buffer
        const rawScale = (patternBox.width - 20) / animWidth;
        const xScale = os.isOthers ? rawScale : Math.max(0.8, rawScale);
        const scaledWidth = animWidth * xScale;
        const applicationLayout = xScale < 1
            ? {
                svgBox: { ...boxDimensions.svgBox, width: scaledWidth },
                patternTransform: `scale(${xScale})`,
                xOrigin: patternBox.x, //  + (animWidth / 2 - (animWidth * xScale) / 2) / xScale,
                yOrigin: lessonsSizes.pattern.yOrigin + (patternBox.height / 2 - (patternBox.height * xScale) / 2) / xScale,
                englishYOffset: 30,
                subtextYOffset: -57, // 68,
                subtextSpace: 0, // hasSubtext ? 19 : 0,
                scale: xScale,
            } : {
                svgBox: { ...boxDimensions.svgBox, width: Math.max(animWidth, boxDimensions.patternBox.width) },
                patternTransform: 'scale(1)',
                xOrigin: patternBox.x,
                yOrigin: lessonsSizes.pattern.yOrigin,
                englishYOffset: 30,
                subtextYOffset: -57, // 68,
                subtextSpace: 0, // hasSubtext ? 19 : 0,
                scale: 1,
            };
        setLayout(applicationLayout);

        // set step state
        setSteps(applicationSteps);

        console.log('steps', applicationSteps);
        console.log('layout', applicationLayout);

        return () => {
            if (setAnimStep) setAnimStep('');
        };

    }, [element]);

    useEffect(() => {
        if (steps.length > 0) {
            // steps ready, set initial step
            onBump(0);
        }

        const handleKeyUp = (e) => {
            if (e.metaKey || e.ctrlKey) {
                if (e.key === 'h') {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowControls(!showControls);
                }
            } else if (document.activeElement.contentEditable !== 'true' && document.activeElement.type !== 'textarea' && steps.length > 0) {
                if (e.key === 'ArrowLeft' && stepIndex.current > 1) {
                    onBump(-1);
                } else if (e.key === 'ArrowRight' && stepIndex.current < steps.length - 1) {
                    onBump(1);
                }
            }
        };
        document.addEventListener('keydown', handleKeyUp);

        if (isMobile && steps.length > 0) {
            player.registerNavigation({
                skipOrNext: () => (stepIndex.current < steps.length - 1 ? 'skipAndNext' : 'replayAndNext'),
                back: () => (stepIndex.current === 0 ? (back ? back(true) : player.back(true)) : onBump(-1)),
                next: () => (stepIndex.current < steps.length - 1 ? onBump(1) : (next ? next(true) : player.next(true))),
                replay: () => onBump(-stepIndex.current),
            });
            player.redrawNavButtons();
        } else if (example && back)
            player.registerNavigation({ back: () => back(true) });

        return () => {
            player.registerNavigation(null);
            document.removeEventListener('keydown', handleKeyUp);
        };

    }, [steps]);

    const speak = (e, step) => {
        e.stopPropagation();
        e.preventDefault();
        playTTS(null, textFromParts(step.field.parts.koreanParts), e);
    };

    const stepNarration = (step) => {
        // first fade out any showing narration, then show current step's narrations
        // all narration domNodes already have correct transition style
        steps.forEach(s => s.narrations && s.narrations.forEach(n => {
            const nEl = n.ref;
            nEl.style.transition = 'opacity 250ms linear';
            nEl.style.opacity = 0;
            nEl.style.transition = 'opacity 250ms linear';
            nEl.parentElement.parentElement.style.visibility = 'hidden';
            // setTimeout(() => nEl.style.display = 'none', 250);
        }));
        // setTimeout(() => steps.forEach(s => s.narrations && s.narrations.forEach(n => n.ref.style.display = 'none')), 250);
        if (step.narrations) {
            step.narrations.forEach((n, i) => {
                // for mobile or vertical orientation, only show on bubble at a time, hide others with display: none so they drop out of the way of subsequent narrations
                if (!(isMobile || orientation === 'vertical') || i === narrationIndex.current) {
                    n.ref.style.display = 'flex';
                    n.ref.parentElement.parentElement.style.visibility = 'visible'; // the other <foreignObject> elements needed to be hidden so pointer events would work on bubble tezt
                    n.ref.style.opacity = 1;
                } else {
                    n.ref.style.display = 'none';
                }
            });
        }
    };

    const stepCommands = (step) => {
        // arrows, flying syllables etc.
        const field = step.field;
        step.ref.style.display = 'block';
        steps.forEach(s => s.field.partsToGo && s.field.partsToGo.forEach(ptg => { if (ptg.arrowRef) ptg.arrowRef.style.opacity = 0; }));
        steps.forEach(s => {
            if (!step.wasInspecting && s.risingJamoRef) {
                s.risingJamoRef.style.opacity = 0;
                s.risingJamoRef.style.transform = `translate(${s.inspectX}px, 0px)`;
                s.risingJamoRef.style.transition = 'all 0ms linear 0ms';
            }
            if (s.abbrevBracketRef) {
                s.abbrevBracketRef.style.opacity = 0;
                s.abbrevBracketRef.style.transition = 'all 0ms linear 0ms';
            }
        });
        if (field.partsToGo) {
            if (step.state !== 'narrative') {
                field.partsToGo.forEach(ptg => {
                    if (ptg.arrowRef) ptg.arrowRef.style.opacity = 1;
                });
                if (field.cmds.after) {
                    setTimeout(() => {
                        field.partsToGo.forEach(ptg => {
                            ptg.part.ref.style.transition = 'transform 0.5s ease-in-out,opacity 0.25s linear 0.25s';
                            ptg.part.ref.style.opacity = 0;
                            ptg.part.ref.style.transform = `translate(${ptg.part.incoming.endX + 50}px, -100px)`;
                            // console.log('ptg', ptg.part.value, ptg.part.ref, getComputedStyle(ptg.part.ref).transition, getComputedStyle(ptg.part.ref).transform);
                        });
                        // remove any subtext marked for flying out
                        field.centeringSections.filter(cs => cs.subtext.ref && cs.subtext.remove).forEach(cs => {
                            cs.subtext.ref.style.transition = 'transform 0.5s ease-in-out,opacity 0.25s linear 0.25s';
                            cs.subtext.ref.style.opacity = 0;
                            cs.subtext.ref.style.transform = `translate(${cs.subtext.incoming.endX + 50}px, -100px)`;
                        });
                        // move the remaining parts to close up gaps
                        field.remainingParts.filter(p => p.ref).forEach(p => {
                            p.ref.style.transition = 'transform 0.5s ease-in-out 0.6s';
                            p.ref.style.transform = `translate(${p.incoming.endX + p.remainingPartsDx}px, 0px)`;
                        });
                        field.partsToGo.forEach(ptg => {
                            if (ptg.arrowRef) ptg.arrowRef.style.opacity = 0;
                        });
                    }, field.cmds.after); // after=x seconds
                }
            } else if (!field.cmds.after) { // partGoes2, flying parts if not auto
                field.partsToGo.forEach(ptg => {
                    ptg.part.ref.style.transition = 'transform 0.5s ease-in-out,opacity 0.25s linear 0.25s';
                    ptg.part.ref.style.opacity = 0;
                    ptg.part.ref.style.transform = `translate(${ptg.part.incoming.endX + 50}px, -100px)`;
                });
                // remove any subtext marked for flying out
                field.centeringSections.filter(cs => cs.subtext.ref && cs.subtext.remove).forEach(cs => {
                    cs.subtext.ref.style.transition = 'transform 0.5s ease-in-out,opacity 0.25s linear 0.25s';
                    cs.subtext.ref.style.opacity = 0;
                    cs.subtext.ref.style.transform = `translate(${cs.subtext.incoming.endX + 50}px, -100px)`;
                });
                // move the remaining parts to close up gaps
                field.remainingParts.filter(p => p.ref).forEach(p => {
                    p.ref.style.transition = 'transform 0.5s ease-in-out 0.6s';
                    p.ref.style.transform = `translate(${p.incoming.endX + p.remainingPartsDx}px, 0px)`;
                });
            }
        }
        if (step.risingJamoRef && step.inspect) {
            step.risingJamoRef.style.transform = `translate(${step.inspectX}px, ${-50}px)`;
            step.risingJamoRef.style.opacity = 1;
            step.risingJamoRef.style.transition = 'opacity 0.6s linear, transform 0.75s ease-in-out';
        }
        if (step.abbrevBracketRef) {
            step.abbrevBracketRef.style.opacity = 1;
            step.abbrevBracketRef.style.transition = 'opacity 1s linear';
        }
    };

    const firstOf = (...args) => args.find(a => typeof a === 'number');

    const setOutgoingStyles = (step) => {
        console.log('  style outgoing', step.state);
        const f = step.field;
        let maxFadeIn = 0;
        f.sections.forEach(s => {
            s.parts.filter(p => p.ref).forEach(p => {
                // establish transitions to outgoing state including any cross-fades
                const fadeDuration = (s.cmds.noFade || p.noFadeOut || p.noFade || p.outgoing.noFade) ? 0 : f.outgoing.fadeDuration;
                const fadeDelay = firstOf(p.fadeOutDelay, f.outgoing.fadeDelay);
                const transform = !p.noMoveOut && `translate(${p.outgoing.endX + (p.remainingPartsDx || 0)}px, 0px)`;
                const transformDuration = p.noMoveOut ? 0 : f.outgoing.transformDuration;
                const transformDelay = firstOf(p.moveOutDelay, f.outgoing.transformDelay);
                // p.ref.style.fill = '#ff0000';
                let transition = '';
                if (fadeDuration + fadeDelay > 0)
                    transition += `opacity ${fadeDuration}ms linear ${fadeDelay}ms`;
                if (transformDuration + transformDelay > 0)
                    transition += `${transition && ', '}transform ${transformDuration}ms ease ${transformDelay}ms`;
                console.log('     ', p.value, 'from', getComputedStyle(p.ref).transition, getComputedStyle(p.ref).transform);
                console.log('           ', 'to', transition || 'all 0ms linear 0ms', transform);
                p.ref.style.transition = transition || 'all 0ms linear 0ms';
                if (transform)
                    p.ref.style.transform = transform;
                p.ref.style.opacity = 0;
                maxFadeIn = Math.max(maxFadeIn, fadeDelay + fadeDuration);
            });
        });
        f.centeringSections.forEach(cs => {
            if (cs.subtext.ref && cs.subtext.outgoing) {
                cs.subtext.ref.style.opacity = 0;
                cs.subtext.ref.style.transform = `translate(${cs.subtext.outgoing.endX}px, 0px)`;
                cs.subtext.ref.style.transition = `opacity ${cs.subtext.fadeDuration}ms linear, transform ${cs.subtext.transformDuration}ms ease`;
            }
        });
        setTimeout(() => {
            if (step.ref && step.ref.style) {
                step.ref.style.display = 'none';
            }
        }, maxFadeIn);
    };

    const setIncomingStyles = (step) => {
        console.log('  style incoming', step.state);
        const f = step.field;
        // displayRef.current.appendChild(step.ref);
        step.ref.style.display = 'block';
        f.sections.forEach(s => {
            s.parts.filter(p => p.ref).forEach(p => {
                // initialize to start pos
                p.ref.style.opacity = 0;
                p.ref.style.transition = 'all 0ms linear 0ms';
                p.ref.style.transform = `translate(${s.cmds.flyIn ? p.incoming.endX + s.cmds.flyIn : p.incoming.startX}px, 0px)`;
                // p.ref.style.fill = '#00ff00';
                // establish transitions to landing state including any cross-fades
                const fadeDuration = (s.cmds.noFade || p.noFadeIn || p.noFade || p.incoming.noFade) ? 0 : firstOf(s.fadeIn, f.incoming.fadeDuration);
                const fadeDelay = firstOf(p.fadeInDelay, s.fadeInDelay, f.incoming.fadeDelay);
                const transform = !p.noMoveIn && `translate(${p.incoming.endX}px, 0px)`;
                const transformDuration = p.noMoveIn ? 0 : firstOf(p.moveInDur, s.moveInDur, f.incoming.transformDuration);
                const transformDelay = firstOf(p.moveInDelay, f.incoming.transformDelay);
                const transition = `opacity ${fadeDuration}ms linear ${fadeDelay}ms, transform ${transformDuration}ms ease ${transformDelay}ms`;
                console.log('     ', p.value, 'from', getComputedStyle(p.ref).transition, getComputedStyle(p.ref).transform);
                console.log('           ', 'to', transition, transform);
                p.ref.style.transition = transition;
                if (transform)
                    p.ref.style.transform = transform;
                p.ref.style.opacity = 1;
            });
        });
        f.centeringSections.forEach(cs => {
            if (cs.subtext.ref) {
                cs.subtext.ref.style.opacity = 0;
                cs.subtext.ref.style.transition = 'all 0ms linear 0ms';
                cs.subtext.ref.style.transform = `translate(${cs.subtext.incoming.startX}px, 0px)`;
                cs.subtext.ref.style.transition = `opacity ${cs.subtext.fadeDuration}ms linear, transform ${cs.subtext.transformDuration}ms ease`;
                cs.subtext.ref.style.transform = `translate(${cs.subtext.incoming.endX}px, 0px)`;
                cs.subtext.ref.style.opacity = 1;
            }
        });
        if (step.speakRef) step.speakRef.style.opacity = 1;
    };

    const hideAll = () => {
        // console.log('hide (reset) all');
        steps.filter(st => st.state !== 'narrative').forEach(st => {
            // console.log('   step', st.state);
            st.field.sections.forEach(s => s.parts.filter(p => p.ref).forEach(p => {
                // console.log('     ', p.value, 'from', getComputedStyle(p.ref).transition, getComputedStyle(p.ref).transform);
                // console.log('            ', 'to', 'all 0ms linear 0ms', `translate(${p.incoming.endX}px, 0px)`);
                p.ref.style.transition = 'all 0ms linear 0ms';
                p.ref.style.transform = `translate(${p.incoming.endX}px, 0px)`;
                p.ref.style.opacity = 0;
            }));
            st.field.centeringSections.forEach(cs => {
                if (cs.subtext.ref) {
                    cs.subtext.ref.style.transition = 'all 0ms linear 0ms';
                    cs.subtext.ref.style.transform = `translate(${cs.subtext.incoming.endX}px, 0px)`;
                    cs.subtext.ref.style.opacity = 0;
                }
            });
            st.ref.style.display = 'none';
            if (st.speakRef) st.speakRef.style.opacity = 0;
        });
    };

    const showIncomingStepBack = (step) => {
        // console.log('show step', step.state);
        step.ref.style.display = 'block';
        if (step.parentStep)
            step.parentStep.ref.style.display = 'block';
        step.field.sections.forEach(s => {
            s.parts.filter(p => p.ref).forEach(p => {
                // console.log('   ', p.value, 'from', getComputedStyle(p.ref).transition, getComputedStyle(p.ref).transform);
                // console.log('           ', 'to', 'all 0ms linear 0ms', `translate(${p.incoming.endX}px, 0px)`);
                p.ref.style.transition = 'all 0ms linear 0ms';
                p.ref.style.transform = `translate(${p.incoming.endX}px, 0px)`;
                p.ref.style.opacity = 1;
            });
        });
        step.field.centeringSections.forEach(cs => {
            if (cs.subtext.ref) {
                cs.subtext.ref.style.transition = 'all 0ms linear 0ms';
                cs.subtext.ref.style.transform = `translate(${cs.subtext.incoming.endX}px, 0px)`;
                cs.subtext.ref.style.opacity = 1;
            }
        });
    };

    // scroll heuristic for long patterns that using a scrolling view to bring into view latest changes to the pattern
    const scrollIntoView = (bump, field) => {
        if (animScroller.current) {
            if (bump === 0 || field.width * layout.scale < animScroller.current.clientWidth)
                animScroller.current.scrollLeft = (animScroller.current.scrollWidth - animScroller.current.clientWidth) / 2;
            else if (field.cmds.flyIn) {
                if (field.cmds.flyIn < 0) {
                    animScroller.current.scrollLeft = field.originX * layout.scale;
                } else {
                    animScroller.current.scrollLeft = animScroller.current.scrollWidth - animScroller.current.clientWidth - field.originX * layout.scale;
                }
            } else if (field.scrollPoint !== undefined) {
                animScroller.current.scrollLeft = field.scrollPoint * layout.scale;
            }
        }
    };

    // bump process step
    const onBump = (bump) => {
        const nextStepIndex = stepIndex.current + bump;
        if (autoFirstStep && bump === 0) {
            setTimeout(() => onBump(1), 10);
        }

        GTM.userAction(`animation-${bump > 0 ? 'forward' : bump < -1 ? 'replay' : 'back'}`, { page: player.currentPage.componentName, pattern: element.title }, 'lessons');

        if (nextStepIndex >= steps.length) {
            if (next) next(true);
        } else if (nextStepIndex < 0) {
            if (back) back(true);
        } else {
            const incomingStep = steps[nextStepIndex];
            let action = 'processNext';

            if (bump >= 0) {
                // play forward
                if (incomingStep.state !== 'narrative') { // action step
                    narrationIndex.current = 0;
                    if (incomingStep.prevActionStep) {
                        // first process outgoing actions
                        setOutgoingStyles(incomingStep.prevActionStep);
                    }
                    scrollIntoView(bump, incomingStep.field);
                    setIncomingStyles(incomingStep);
                } else {
                    narrationIndex.current += 1;
                }
            } else {
                // backward hides all parts, then shows prior step parts with no transitions
                hideAll();
                showIncomingStepBack(incomingStep);
                action = 'processBack';
                if (incomingStep.state === 'narrative')
                    narrationIndex.current = incomingStep.narrations.length - 1;
                else {
                    if (narrationIndex.current > 0)
                        narrationIndex.current -= 1;
                    scrollIntoView(bump, incomingStep.field);
                }
            }

            // update help guidelines (for dev only, remove for production)
            // document.querySelectorAll('.help-line').forEach(n => n.style.display = 'all 0ms linear 0ms');
            // document.querySelectorAll(`.line-${step.field.name}`).forEach(n => n.style.display = 'initial');

            // hide prior & show current step narration & other animation adornments
            stepNarration(incomingStep);
            // step any involved commands, such as removing 다, inspecting jamo, syllable-blend animations, fly-ins, etc
            stepCommands(incomingStep);
            //
            stepIndex.current = nextStepIndex;
            // post step as lesson event - we may not be able do this generally, the event causes the progress context to update and anything refering to it will re-render & restart the animatiom!   Figure how to prevent this.
            if (bump !== 0) {
                const event = { action, details: { step: nextStepIndex, state: steps[nextStepIndex].state } };
                player.postEvent(event, element);
            }
            if (stepIndex.current === steps.length - 1) {
                // last step => process sequence complete
                player.postEvent({ action: 'complete' }, element);
            }
            if (setAnimStep) {
                setAnimStep(steps[nextStepIndex].field.name);
            }
            // record step in URL
            if (bump !== 0) {
                const queryParams = [`as=${nextStepIndex}`];
                if (example) queryParams.push(`ex=${element.index}`);
                window.history.replaceState(null, '', player.makePageURL('.', '.', queryParams));
            }
        }
    };

    // renders parts for all steps at once, hiding all wih 0 opacity. User-stepping selectively transitions a step's parts into view
    return steps.length > 0 ? (
        <PatternStage geom={boxDimensions}>
            <AnimScroller ref={animScroller} geom={boxDimensions.patternBox}>
                <AnimSVG ref={setRef} startHidden={startHidden} width={layout.svgBox.width} height={layout.svgBox.height} xmlns="http://www.w3.org/1999/xhtml">
                    <g transform={layout.patternTransform}>
                        { steps.map(step => (
                            <StepGroup key={step.index} ref={ref => step.ref = ref} step={step.state}>
                                { step.field.partsToGo && step.state !== 'narrative' && step.field.partsToGo.map((ptg, i) => ptg.part.showArrow && (
                                    <StemifyArrow
                                        key={i} className={step.state} ref={ref => ptg.arrowRef = ref} href={stemifyArrow} step={step}
                                        x={layout.xOrigin + ptg.part.incoming.endX + ptg.part.metrics.width / 2} y={layout.yOrigin - (95 + ptg.part.arrowOffset)} />
                                ))}
                                { step.inspectX !== undefined && (
                                    <RisingJamo
                                        className={step.state} f={step.field} nodeRef={ref => step.risingJamoRef = ref}
                                        x={layout.xOrigin} y={layout.yOrigin} step={step}
                                    >
                                        { step.field.inspectJamo }
                                    </RisingJamo>
                                )}
                                { step.state === 'checkAbbreviation' && (
                                    <AbbreviationBracket
                                        className={step.state} ref={ref => step.abbrevBracketRef = ref} href={step.field.bracketHref}
                                        step={step} x={layout.xOrigin + step.checkX + 1} y={layout.yOrigin - 60}
                                    />
                                )}
                                { step.state !== 'narrative' && step.field.centeringSections.map(cs => cs.subtext.text && (
                                    <SubtextGroup className={step.state} key={cs.subtext.text} transform={`translate(${layout.xOrigin}, ${layout.yOrigin + layout.subtextYOffset})`}>
                                        <Subtext nodeRef={ref => cs.subtext.ref = ref} subtext={cs.subtext} key={cs.subtext.text} step={step} />
                                    </SubtextGroup>
                                ))}
                                {step.field.parts.englishParts && step.state !== 'narrative' && (
                                    <EnglishParts className={step.state} transform={`translate(${layout.xOrigin}, ${layout.yOrigin + layout.englishYOffset})`}>
                                        { step.field.parts.englishParts.map((part, i) => (
                                            <PhraseWord key={i} nodeRef={ref => part.ref = ref} part={part} element={element} />
                                        ))}
                                    </EnglishParts>
                                )}
                                { step.field.parts.koreanParts && step.state !== 'narrative' && (
                                    <KoreanParts id={`pa-kt-${step.field.index}`} className={step.state} transform={`translate(${layout.xOrigin}, ${layout.yOrigin})`}>
                                        { step.index === element.lastKoreanPartStep && <SpeakIcon ref={r => step.speakRef = r} x={(layout.svgBox.width / 2 - 13) / layout.scale} y={-78} href={speakIcon} onClick={e => speak(e, step)} /> }
                                        { step.field.parts.koreanParts.map((part, i) => (
                                            <PhraseWord key={i} nodeRef={ref => part.ref = ref} part={part} element={element} />
                                        ))}
                                        {/*<TextTools svgID={`pa-kt-${step.field.index}`} text={textFromParts(step.field.parts.koreanParts)} />*/}
                                    </KoreanParts>
                                )}
                            </StepGroup>
                        ))}
                    </g>
                </AnimSVG>
            </AnimScroller>
            { steps.map(step => (
                <React.Fragment key={step.index}>
                    { step.narrations && (
                        <NarrationParts geom={{ ...boxDimensions.narrationBox, y: boxDimensions.narrationBox.y + (orientation === 'vertical' ? layout.subtextSpace : 0) }}>
                            <NarrationBox>
                                { step.narrations.map((part, i) => <Narration key={i} nodeRef={ref => part.ref = ref} index={i} part={part} box={boxDimensions} orientation={orientation} />) }
                            </NarrationBox>
                        </NarrationParts>
                    )}
                </React.Fragment>
            ))}
            { showControls && !isMobile && steps.length > 0 && (
                <ControlRow geom={{ ...boxDimensions.controls, y: boxDimensions.controls.y + layout.subtextSpace }}>
                    <Stepper steps={steps} stepIndex={stepIndex} onNext={() => onBump(1)} onBack={() => onBump(-1)} onReplay={() => onBump(-stepIndex.current)} />
                </ControlRow>
            )}
        </PatternStage>
    ) : null;
};

registerHelp(PatternAnimation, [
    <HelpBubble
        title="Click to step forward"
        desktop={{ anchor: '.step-fwd', titleAt: 'top', arrowPos: 'left', arrowDir: 'up', style: { top: 49, left: 6, contentWidth: 230 } }}>
        <p>Click this button to go forward one step in the animated pattern explanation.</p>
    </HelpBubble>,
    <HelpBubble
        title="Click to replay"
        desktop={{ anchor: '.step-replay', titleAt: 'top', arrowPos: 'left', arrowDir: 'up', style: { top: 49, left: 6, contentWidth: 230 } }}>
        <p>Click this button to restart the animated explanation from the beginning.</p>
    </HelpBubble>,
    <HelpBubble
        title="Click to step backward"
        desktop={{ anchor: '.step-back', titleAt: 'top', arrowPos: 'right', arrowDir: 'up', style: { top: 49, right: 6, contentWidth: 230 } }}>
        <p>Click this button to go back one step, so you can review the explanation steps as often as you like.</p>
    </HelpBubble>,
    // mobile:
    <HelpBubble
        title="Pattern animation controls"
        mobile={{ anchor: '#next-btn', titleAt: 'bottom', arrowPos: 'right', arrowDir: 'down', style: { bottom: 70, right: 45, contentWidth: 205 } }}>
        <p>Click the <strong>right</strong> arrow to step forward in the animated explanation.</p>
        <p>Click the <strong>left</strong> arrow to step backward in the animated explanation.</p>
        <p>Click the <strong>Skip</strong> button to skip the rest of the animation.</p>
    </HelpBubble>,
]);

export default PatternAnimation;
